import { i18n } from "@lingui/core";

export const defaultLocale = "en";

/** The locales we ship compiled catalogs for (see lingui.config.ts). */
export const supportedLocales = ["en", "sv", "nb"] as const;

/**
 * Clamp an arbitrary locale to one we ship a catalog for, falling back to
 * {@link defaultLocale}. The locale ultimately comes from the deploy environment
 * (`NMP_LANGUAGE`), so an unexpected value (e.g. `da`, `no`, or a regional
 * `en-US`) must degrade gracefully rather than make the dynamic catalog import
 * throw and fail the request.
 */
export function resolveLocale(locale: string): string {
	return (supportedLocales as readonly string[]).includes(locale) ? locale : defaultLocale;
}

/**
 * Load a locale's compiled catalog and activate it on the shared global `i18n`
 * instance. The locale is clamped via {@link resolveLocale} first, so an
 * unsupported value safely falls back instead of throwing. Side-effecting:
 * mutates that singleton, so every later `i18n` consumer (SSR and hydration) sees
 * the new locale. Invoked from {@link definePageRoute} before a page renders.
 */
export async function loadAndActivateLocale(locale: string) {
	const resolved = resolveLocale(locale);
	const { messages } = await import(`../../locales/${resolved}.mjs`);
	i18n.load(resolved, messages);
	i18n.activate(resolved);
}
